import type { SVGProps } from 'react';
import { VolatilityLowIcon } from '@/app/elements/svg/icons/volatility-low';
import { VolatilityMediumIcon } from '@/app/elements/svg/icons/volatility-medium';
import { VolatilityHighIcon } from '@/app/elements/svg/icons/volatility-high';

export type TagItem = {
    [key: string]: {
        uid: string;
    };
};

export type VolatilityIconProps = SVGProps<SVGSVGElement> & {
    fill?: string;
};

export type VolatilityConfig = {
    icon: React.FC<VolatilityIconProps>;
    display: string;
    alt: string;
    keywords: string[];
};

export const VOLATILITY_CONFIG: Record<string, VolatilityConfig> = {
    LOW: {
        icon: VolatilityLowIcon,
        display: 'Low',
        alt: 'Low volatility',
        keywords: ['low', 'low-volatility', 'volatility-low']
    },
    MEDIUM: {
        icon: VolatilityMediumIcon,
        display: 'Medium',
        alt: 'Medium volatility',
        keywords: ['medium', 'med', 'medium-volatility', 'volatility-medium', 'volatility-med']
    },
    HIGH: {
        icon: VolatilityHighIcon,
        display: 'High',
        alt: 'High volatility',
        keywords: ['high', 'high-volatility', 'volatility-high']
    }
};

/**
 * Returns the volatility configuration based on the volatility value.
 */
export const getVolatilityConfig = (volatility: string | null | undefined): VolatilityConfig | null => {
    if (!volatility) {
        return null;
    }

    const normalizedVolatility = volatility.toLowerCase();

    const matchedConfig = Object.values(VOLATILITY_CONFIG).find((config) =>
        config.keywords.includes(normalizedVolatility)
    );

    return matchedConfig || null;
};

/**
 * Formats a UID by removing a prefix and converting to title case.
 * Example: "theme-ancient-egypt" with prefix "theme-" becomes "Ancient Egypt"
 */
export const formatUid = (uid: string | undefined, prefix: string): string => {
    if (!uid || typeof uid !== 'string') {
        return '';
    }

    return uid
        .replace(prefix, '')
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * Checks if a UID is valid (non-empty string).
 */
export const isValidUid = (uid: unknown): uid is string => typeof uid === 'string' && uid.trim().length > 0;

/**
 * Extracts the UID from a tag item object.
 */
export const getItemUid = (item: TagItem): string | undefined => {
    const [itemKey] = Object.keys(item);
    return item[itemKey]?.uid;
};

/**
 * Removes duplicate items from a TagItem array based on UID.
 */
export const deduplicateTagItems = (items: TagItem[]): TagItem[] => {
    if (!Array.isArray(items)) {
        return [];
    }

    const seen = new Set<string>();
    return items.filter((item) => {
        if (!item) {
            return false;
        }
        const uid = getItemUid(item);
        if (!uid || seen.has(uid)) {
            return false;
        }
        seen.add(uid);
        return true;
    });
};

// Aliases for backwards compatibility
export const deduplicateThemes = deduplicateTagItems;
export const deduplicateFeatures = deduplicateTagItems;
